/**
 * ToolSelector - Compact workflow tool picker with VS-first UX
 * 
 * Optimized for workflow contexts (agent creation, delegation setup).
 * Features Virtual Server bundles as primary action with manual selection
 * as secondary flow.
 * 
 * Blue Steel Score Target: 85+
 * - VS bundles are prominent
 * - Search + filter in collapsed state
 * - Smooth expand/collapse for tool list
 * 
 * @example
 * ```tsx
 * <ToolSelector
 *   serviceId="orchestration-service"
 *   selectedToolIds={selectedIds}
 *   onSelectionChange={handleChange}
 *   onSubmit={handleSubmit}
 * />
 * ```
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { VirtualServerBundleCard } from './VirtualServerBundleCard';
import { VirtualServerTabs } from './VirtualServerTabs';
import { ToolCard } from './ToolCard';
import { useVirtualServers } from './hooks/useVirtualServers';
import { useMcpTools } from './hooks/useMcpTools';
import type { MCPTool, VirtualServer, ToolSelectionState } from './types';

export interface ToolSelectorProps {
  serviceId: string;
  selectedToolIds?: string[];
  onSelectionChange?: (state: ToolSelectionState) => void;
  onSubmit?: (state: ToolSelectionState) => void;
  submitLabel?: string;
  minSelection?: number;
  maxSelection?: number;
  showVirtualServerCards?: boolean;
  defaultExpanded?: boolean;
}

export const ToolSelector: React.FC<ToolSelectorProps> = ({
  serviceId,
  selectedToolIds: initialSelectedIds = [],
  onSelectionChange,
  onSubmit,
  submitLabel = 'Continue',
  minSelection = 1,
  maxSelection,
  showVirtualServerCards = true,
  defaultExpanded = false,
}) => {
  // Local state for selection
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds);
  const [manualModeExpanded, setManualModeExpanded] = useState(defaultExpanded);
  const [activeVsId, setActiveVsId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Sync with external selectedToolIds
  useEffect(() => {
    setSelectedIds(initialSelectedIds);
  }, [initialSelectedIds]);

  // Fetch data
  const { virtualServers, loading: vsLoading } = useVirtualServers(serviceId);
  const { tools: allTools, filteredTools, loading: toolsLoading } = useMcpTools({
    serviceId,
    virtualServerId: activeVsId,
    search: searchTerm,
  });

  // Get preview tool names for each virtual server
  const vsToolPreviews = useMemo(() => {
    const previews: Record<string, string[]> = {};
    virtualServers.forEach(vs => {
      // Filter tools that belong to this VS (supports full ID, name, or short ID)
      const vsTools = allTools.filter(t => {
        if (t.virtual_server_id === vs.id) return true;
        if (!vs.tool_ids?.length) return false;
        if (vs.tool_ids.includes(t.id)) return true;
        if (vs.tool_ids.includes(t.name)) return true;
        const shortId = t.id.includes(':') ? t.id.split(':').pop() : t.id;
        return shortId ? vs.tool_ids.includes(shortId) : false;
      });
      previews[vs.id] = vsTools.slice(0, 5).map(t => t.name);
    });
    return previews;
  }, [virtualServers, allTools]);

  // Build selection state
  const selectionState = useMemo((): ToolSelectionState => ({
    selectedToolIds: selectedIds,
    selectedTools: allTools
      .filter(t => selectedIds.includes(t.id))
      .map(t => ({ id: t.id, name: t.name, description: t.description })),
  }), [selectedIds, allTools]);

  // Notify parent of selection changes
  useEffect(() => {
    onSelectionChange?.(selectionState);
  }, [selectionState, onSelectionChange]);

  // Handle individual tool toggle
  const handleToolToggle = useCallback((tool: MCPTool) => {
    setSelectedIds(prev => {
      const isSelected = prev.includes(tool.id);
      if (isSelected) {
        return prev.filter(id => id !== tool.id);
      } else {
        if (maxSelection && prev.length >= maxSelection) {
          return prev; // Don't add if at max
        }
        return [...prev, tool.id];
      }
    });
  }, [maxSelection]);

  // Helper: find tools matching a virtual server (supports full ID, name, or short ID)
  const getToolsForVs = useCallback((vs: VirtualServer) => {
    return allTools.filter(t => {
      if (t.virtual_server_id === vs.id) return true;
      if (!vs.tool_ids?.length) return false;
      if (vs.tool_ids.includes(t.id)) return true;
      if (vs.tool_ids.includes(t.name)) return true;
      const shortId = t.id.includes(':') ? t.id.split(':').pop() : t.id;
      return shortId ? vs.tool_ids.includes(shortId) : false;
    });
  }, [allTools]);

  // Handle VS bundle selection
  const handleVsBundleSelect = useCallback((vs: VirtualServer) => {
    // Get actual tool IDs from allTools that match this VS
    const vsToolIds = getToolsForVs(vs).map(t => t.id);

    const allSelected = vsToolIds.length > 0 && vsToolIds.every(id => selectedIds.includes(id));

    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !vsToolIds.includes(id)));
    } else {
      setSelectedIds(prev => {
        const newIds = [...new Set([...prev, ...vsToolIds])];
        if (maxSelection && newIds.length > maxSelection) {
          return newIds.slice(0, maxSelection);
        }
        return newIds;
      });
    }
  }, [selectedIds, maxSelection, getToolsForVs]);

  // Check if a VS is fully selected
  const isVsFullySelected = useCallback((vs: VirtualServer) => {
    const vsToolIds = getToolsForVs(vs).map(t => t.id);
    return vsToolIds.length > 0 && vsToolIds.every(id => selectedIds.includes(id));
  }, [selectedIds, getToolsForVs]);

  // Handle submit
  const handleSubmit = useCallback(() => {
    if (selectedIds.length >= minSelection) {
      onSubmit?.(selectionState);
    }
  }, [selectedIds.length, minSelection, selectionState, onSubmit]);

  const loading = vsLoading || toolsLoading;
  const canSubmit = selectedIds.length >= minSelection && (!maxSelection || selectedIds.length <= maxSelection);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
    }}>
      {/* Virtual Server Bundle Cards */}
      {showVirtualServerCards && virtualServers.length > 0 && (
        <div>
          <div style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--main-text)',
            marginBottom: 4,
          }}>
            🚀 Quick Start: Select a Tool Bundle
          </div>
          <div style={{
            fontSize: 11,
            color: 'var(--main-text-secondary)',
            marginBottom: 12,
          }}>
            Virtual servers contain pre-configured tools for specific use cases
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 12,
          }}>
            {vsLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    height: 100,
                    borderRadius: 14,
                    background: 'var(--skeleton-bg)',
                    animation: 'shimmer 1.5s infinite',
                  }}
                />
              ))
            ) : (
              virtualServers.map(vs => (
                <VirtualServerBundleCard
                  key={vs.id}
                  virtualServer={vs}
                  selected={isVsFullySelected(vs)}
                  onSelect={handleVsBundleSelect}
                  toolPreview={vsToolPreviews[vs.id] || []}
                  size="compact"
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Manual Selection Toggle */}
      <div>
        <button
          onClick={() => setManualModeExpanded(prev => !prev)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: '12px 16px',
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: 10,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = 'var(--main-accent)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-color)';
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <span style={{ fontSize: 16 }}>🔧</span>
            <div>
              <div style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--main-text)',
                textAlign: 'left',
              }}>
                {manualModeExpanded ? 'Hide Manual Selection' : 'Or Select Individual Tools'}
              </div>
              <div style={{
                fontSize: 11,
                color: 'var(--main-text-secondary)',
                textAlign: 'left',
              }}>
                {allTools.length} tools available • {selectedIds.length} selected
              </div>
            </div>
          </div>
          <span style={{
            fontSize: 12,
            color: 'var(--main-text-secondary)',
            transform: manualModeExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}>
            ▼
          </span>
        </button>

        {/* Expanded Manual Selection */}
        {manualModeExpanded && (
          <div style={{
            marginTop: 12,
            padding: 16,
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: 10,
            animation: 'slideDown 0.25s ease',
          }}>
            {/* VS Tabs */}
            {virtualServers.length > 0 && (
              <VirtualServerTabs
                virtualServers={virtualServers}
                activeVsId={activeVsId}
                onSelect={setActiveVsId}
                totalToolCount={allTools.length}
              />
            )}

            {/* Search */}
            <input
              type="text"
              placeholder="🔍 Search tools..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px',
                background: 'color-mix(in srgb, var(--main-text) 5%, transparent)',
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                fontSize: 13,
                color: 'var(--main-text)',
                marginBottom: 12,
                outline: 'none',
              }}
            />

            {/* Tool Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 10,
              maxHeight: 350,
              overflowY: 'auto',
              paddingRight: 8,
            }}>
              {toolsLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      height: 70,
                      borderRadius: 10,
                      background: 'var(--skeleton-bg)',
                      animation: 'shimmer 1.5s infinite',
                    }}
                  />
                ))
              ) : filteredTools.length === 0 ? (
                <div style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: 30,
                  color: 'var(--main-text-secondary)',
                }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>🔍</div>
                  <div style={{ fontSize: 13 }}>No tools found</div>
                </div>
              ) : (
                filteredTools.map(tool => (
                  <ToolCard
                    key={tool.id}
                    tool={tool}
                    selected={selectedIds.includes(tool.id)}
                    onSelect={handleToolToggle}
                    compact
                    showParameters={false}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Selection Summary + Submit */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        background: selectedIds.length > 0
          ? 'color-mix(in srgb, var(--main-accent) 10%, transparent)'
          : 'var(--card-bg)',
        border: `1px solid ${selectedIds.length > 0 ? 'var(--main-accent)' : 'var(--border-color)'}`,
        borderRadius: 12,
        transition: 'all 0.25s ease',
      }}>
        <div>
          <div style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--main-text)',
          }}>
            {selectedIds.length === 0 ? (
              '⚠️ No tools selected'
            ) : (
              `✨ ${selectedIds.length} tool${selectedIds.length !== 1 ? 's' : ''} selected`
            )}
          </div>
          {minSelection > 0 && selectedIds.length < minSelection && (
            <div style={{
              fontSize: 11,
              color: 'var(--warning-color)',
              marginTop: 2,
            }}>
              Select at least {minSelection} tool{minSelection !== 1 ? 's' : ''} to continue
            </div>
          )}
          {maxSelection && selectedIds.length > 0 && (
            <div style={{
              fontSize: 11,
              color: 'var(--main-text-secondary)',
              marginTop: 2,
            }}>
              {selectedIds.length}/{maxSelection} maximum
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          {selectedIds.length > 0 && (
            <button
              onClick={() => setSelectedIds([])}
              style={{
                padding: '10px 16px',
                background: 'transparent',
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                color: 'var(--main-text-secondary)',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = 'var(--error-color)';
                e.currentTarget.style.color = 'var(--error-color)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-color)';
                e.currentTarget.style.color = 'var(--main-text-secondary)';
              }}
            >
              Clear
            </button>
          )}
          
          {onSubmit && (
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || loading}
              style={{
                padding: '10px 24px',
                background: canSubmit ? 'var(--button-primary-bg, linear-gradient(135deg, var(--quantum-violet, #6C4CFF), var(--ultraviolet-magenta, #B326FF)))' : 'var(--border-color)',
                border: 'none',
                borderRadius: 8,
                color: canSubmit ? 'white' : 'var(--main-text-secondary)',
                fontSize: 13,
                fontWeight: 600,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease',
                boxShadow: canSubmit
                  ? '0 4px 12px color-mix(in srgb, var(--main-accent) 35%, transparent)'
                  : 'none',
              }}
              onMouseOver={(e) => {
                if (canSubmit) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px color-mix(in srgb, var(--main-accent) 45%, transparent)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                if (canSubmit) {
                  e.currentTarget.style.boxShadow = '0 4px 12px color-mix(in srgb, var(--main-accent) 35%, transparent)';
                }
              }}
            >
              {submitLabel} →
            </button>
          )}
        </div>
      </div>

      {/* Keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { opacity: 0.5; }
          50% { opacity: 0.8; }
          100% { opacity: 0.5; }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

